/**
 * Mitarbeiter-Anlage — Vollbild-Page mit Tabs Stammdaten + Zugriffe.
 *
 * Anlage-Variante von AdressbuchPersonAkte.tsx — bewusst eine eigene
 * Komponente statt Mode-Flag in der Akte, weil die Anlage andere Pflichten
 * hat (Passwort Pflicht, kein User-/Person-Datensatz vorhanden, Sicherheit
 * und Akte erst nach Speichern sinnvoll). Layout + Optik orientieren sich
 * an der Akte (Sticky-Header, Tab-Bar, Section-Karten).
 *
 * Ablauf:
 *   1. Admin fuellt Pflichtfelder (Anrede, Vor-/Nachname, E-Mail, Passwort)
 *      und optional Telefon/Mobil/Geburtstag/Anschrift/Notfallkontakt.
 *   2. Auf Zugriffe-Tab waehlt Admin Rolle + Adressbuch-Gruppen.
 *   3. Speichern: POST /admin/adressbuch/persons → person_id zurueck.
 *   4. Wenn Gruppen ausgewaehlt: PUT /admin/adressbuch/persons/:id/groups.
 *   5. Navigieren zur Akte (/admin/adressbuch/persons/:id) — dort kann der
 *      Admin die restlichen Sektionen pflegen.
 *
 * Modul-ACL-Overrides sind in der Anlage nicht editierbar (Hinweis-Karte) —
 * die ACL-Liste kommt erst nach dem ersten Detail-Load aus der Self-Registry.
 *
 * Nur fuer isAdmin aufrufbar — ProtectedRoute klemmt zusaetzlich serverseitig.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, AlertCircle } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

interface AddressbookGroup {
  id: string;
  name: string;
  description?: string | null;
}

interface PersonCreateResponse {
  success: boolean;
  data?: { person_id: string | null; user_id: string };
  error?: string;
}

type Salutation = '' | 'herr' | 'frau' | 'divers';
type UserRole = 'handwerker' | 'vorarbeiter' | 'buero' | 'gf' | 'admin' | 'softwaretester';

interface FormState {
  salutation: Salutation;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  mobile: string;
  birthday: string;
  street: string;
  streetExtra: string;
  zip: string;
  city: string;
  countryCode: string;
  emergencyName: string;
  emergencyRelation: string;
  emergencyPhone: string;
  emergencyEmail: string;
  role: UserRole;
  weeklyHours: string;
  hourlyRate: string;
  active: boolean;
}

const INITIAL_FORM: FormState = {
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
  mobile: '',
  birthday: '',
  street: '',
  streetExtra: '',
  zip: '',
  city: '',
  countryCode: 'DE',
  emergencyName: '',
  emergencyRelation: '',
  emergencyPhone: '',
  emergencyEmail: '',
  role: 'handwerker',
  weeklyHours: '40',
  hourlyRate: '',
  active: true,
};

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Administrator',
  gf: 'Geschäftsführung',
  buero: 'Büro',
  vorarbeiter: 'Vorarbeiter',
  handwerker: 'Handwerker',
  softwaretester: 'Softwaretester',
};
const ALL_ROLES: UserRole[] = ['handwerker', 'vorarbeiter', 'buero', 'gf', 'admin', 'softwaretester'];

const TAB_DEF = [
  { key: 'stammdaten' as const, label: 'Stammdaten' },
  { key: 'zugriffe' as const,   label: 'Zugriffe' },
  { key: 'sicherheit' as const, label: 'Sicherheit' },
  { key: 'akte' as const,       label: 'Akte' },
];
type TabKey = (typeof TAB_DEF)[number]['key'];

const inp = 'w-full px-3 py-2 text-sm rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:border-brand-500 focus:outline-none disabled:opacity-60';
const inpError = 'w-full px-3 py-2 text-sm rounded-lg bg-slate-800 border border-rose-500 text-slate-100 focus:border-rose-400 focus:outline-none';
// iOS Safari Auto-Zoom-Block bei < 16px Input-Schriftgroesse (Memory
// feedback_ios_safari_input_fontsize). Wird auf alle Eingabefelder
// dieser Page als style angehaengt.
const inputStyle: React.CSSProperties = { fontSize: '16px' };

function Field({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string | null; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <div className="mt-1 text-xs text-rose-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

export default function AdressbuchPersonNeuPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [allGroups, setAllGroups] = useState<AddressbookGroup[]>([]);
  const [tab, setTab] = useState<TabKey>('stammdaten');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Defensive: ProtectedRoute soll das eigentlich verhindern, aber wer das
  // direkt aufruft soll nicht in einem halbgaren Formular landen.
  useEffect(() => {
    if (!isAdmin) navigate('/uebersicht/adressbuch', { replace: true });
  }, [isAdmin, navigate]);

  useEffect(() => {
    api.get<{ success: boolean; data: AddressbookGroup[] }>('/admin/adressbuch/groups')
      .then((r) => setAllGroups(r.data ?? []))
      .catch((e) => { console.error('[AdressbuchPersonNeu] groups load failed', e); setAllGroups([]); });
  }, []);

  const setField = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) => {
    setTouched(true);
    setForm((prev) => ({ ...prev, [k]: v }));
    if (k === 'email') setEmailError(null);
    if (serverError) setServerError(null);
  }, [serverError]);

  const toggleGroup = (id: string) => {
    setTouched(true);
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Validierung — clientseitig, vor dem Submit. Inline an den Feldern.
  const validation = useMemo(() => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.salutation) errors.salutation = 'Pflichtfeld';
    if (!form.firstName.trim()) errors.firstName = 'Pflichtfeld';
    if (!form.lastName.trim()) errors.lastName = 'Pflichtfeld';
    const emailTrim = form.email.trim();
    if (!emailTrim) {
      errors.email = 'Pflichtfeld';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }
    if (form.password.length < 8) errors.password = 'Mindestens 8 Zeichen';
    if (form.weeklyHours !== '' && Number.isNaN(Number(form.weeklyHours))) {
      errors.weeklyHours = 'Bitte Zahl eingeben';
    }
    if (form.hourlyRate !== '' && Number.isNaN(Number(form.hourlyRate))) {
      errors.hourlyRate = 'Bitte Zahl eingeben';
    }
    if (form.emergencyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emergencyEmail.trim())) {
      errors.emergencyEmail = 'Ungültige E-Mail-Adresse';
    }
    return errors;
  }, [form]);

  const isValid = Object.keys(validation).length === 0;

  // Zeigt Stammdaten-Fehler? -> kleiner Badge am Tab
  const stammdatenInvalid = ['salutation', 'firstName', 'lastName', 'email', 'password']
    .some((k) => k in validation);

  const handleCancel = () => {
    if (touched) {
      // Bewusst nativer confirm — keine globale ConfirmModal-Komponente im
      // Portal, und diese Page hat genau diesen einen Punkt an dem Bestaetigung
      // sinnvoll ist. Folge-Welle kann auf globalen Modal umstellen, sobald er
      // existiert (Memory feedback_modal_statt_native_dialog).
      const ok = window.confirm('Eingaben verwerfen und zurück?');
      if (!ok) return;
    }
    navigate('/uebersicht/adressbuch');
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setServerError(null);
    setEmailError(null);
    try {
      const body: Record<string, unknown> = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        active: form.active,
      };
      if (form.weeklyHours !== '') body['weeklyHours'] = Number(form.weeklyHours);
      if (form.hourlyRate !== '') body['hourlyRate'] = Number(form.hourlyRate);

      const created = await api.post<PersonCreateResponse>('/admin/adressbuch/persons', body);
      const newPersonId = created.data?.person_id;
      if (!newPersonId) {
        // Backend hat den User angelegt, konnte aber die Person-ID nicht
        // nachladen. Wir landen im Adressbuch — die Person ist trotzdem da.
        navigate('/uebersicht/adressbuch');
        return;
      }

      // Stammdaten-Patch fuer die Felder, die der POST-Endpoint nicht
      // entgegennimmt (Anrede, Telefon, Mobil, Geburtstag, Anschrift,
      // Notfallkontakt). Section-Write-Recht ist fuer Admin gegeben.
      const patchBody: Record<string, unknown> = {};
      if (form.salutation) patchBody['salutation'] = form.salutation;
      if (form.phone.trim()) patchBody['phone'] = form.phone.trim();
      if (form.mobile.trim()) patchBody['mobile'] = form.mobile.trim();
      if (form.birthday) patchBody['birthday'] = form.birthday;
      if (form.street.trim()) patchBody['street'] = form.street.trim();
      if (form.streetExtra.trim()) patchBody['streetExtra'] = form.streetExtra.trim();
      if (form.zip.trim()) patchBody['zip'] = form.zip.trim();
      if (form.city.trim()) patchBody['city'] = form.city.trim();
      if (form.countryCode) patchBody['countryCode'] = form.countryCode;
      if (form.emergencyName.trim()) patchBody['emergencyName'] = form.emergencyName.trim();
      if (form.emergencyRelation.trim()) patchBody['emergencyRelation'] = form.emergencyRelation.trim();
      if (form.emergencyPhone.trim()) patchBody['emergencyPhone'] = form.emergencyPhone.trim();
      if (form.emergencyEmail.trim()) patchBody['emergencyEmail'] = form.emergencyEmail.trim();
      if (Object.keys(patchBody).length > 0) {
        try {
          await api.put(`/admin/adressbuch/persons/${newPersonId}`, patchBody);
        } catch (e) {
          // Nicht harter Fehler — Person ist angelegt, Admin sieht die Felder
          // in der Akte und kann sie dort nachpflegen.
          console.error('[AdressbuchPersonNeu] Stammdaten-Patch fehlgeschlagen', e);
        }
      }

      if (selectedGroupIds.size > 0) {
        try {
          await api.put(`/admin/adressbuch/persons/${newPersonId}/groups`, {
            groupIds: Array.from(selectedGroupIds),
          });
        } catch (e) {
          console.error('[AdressbuchPersonNeu] Gruppen-Zuweisung fehlgeschlagen', e);
        }
      }

      setTouched(false);
      navigate(`/admin/adressbuch/persons/${newPersonId}`);
    } catch (e) {
      const msg = (e as Error).message;
      console.error('[AdressbuchPersonNeu] POST /persons fehlgeschlagen', msg);
      if (/E-?Mail.*vergeben/i.test(msg)) {
        setEmailError('Diese E-Mail-Adresse ist bereits vergeben.');
        setTab('stammdaten');
      } else {
        setServerError(msg || 'Anlage fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return <div className="p-6 text-slate-400">Lädt…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4">
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <button onClick={handleCancel}
                  className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200">
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100">Neue Person anlegen</h1>
          <div className="flex-1" />
          <button onClick={handleCancel} disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 disabled:opacity-40">
            <X className="w-4 h-4" /> Abbrechen
          </button>
          <button onClick={handleSave} disabled={!isValid || saving}
                  title={!isValid ? 'Pflichtfelder ausfüllen' : 'Speichern'}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-brand-600 hover:bg-brand-500 text-primary-foreground disabled:opacity-40">
            <Save className="w-4 h-4" /> {saving ? 'Legt an…' : 'Speichern'}
          </button>
        </div>
        <nav className="flex mt-3 -mb-3 border-b-0">
          {TAB_DEF.map((t) => {
            const isActive = tab === t.key;
            const hasError = t.key === 'stammdaten' && stammdatenInvalid && touched;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                        isActive
                          ? 'border-brand-500 text-brand-300'
                          : 'border-transparent text-slate-400 hover:text-slate-200'
                      }`}>
                {t.label}
                {hasError && <span className="ml-1 text-rose-400">•</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {serverError && (
        <div className="px-3 py-2 rounded-lg text-sm bg-rose-500/10 text-rose-300 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{serverError}</div>
        </div>
      )}

      {tab === 'stammdaten' && (
        <div className="space-y-4">
          <Section title="Personalien">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Anrede" required error={touched ? validation.salutation : undefined}>
                <select className={touched && validation.salutation ? inpError : inp}
                        style={inputStyle}
                        value={form.salutation}
                        onChange={(e) => setField('salutation', e.target.value as Salutation)}>
                  <option value="">–</option>
                  <option value="herr">Herr</option>
                  <option value="frau">Frau</option>
                  <option value="divers">Divers</option>
                </select>
              </Field>
              <Field label="Vorname" required error={touched ? validation.firstName : undefined}>
                <input className={touched && validation.firstName ? inpError : inp}
                       style={inputStyle}
                       value={form.firstName}
                       onChange={(e) => setField('firstName', e.target.value)}
                       autoComplete="given-name" />
              </Field>
              <Field label="Nachname" required error={touched ? validation.lastName : undefined}>
                <input className={touched && validation.lastName ? inpError : inp}
                       style={inputStyle}
                       value={form.lastName}
                       onChange={(e) => setField('lastName', e.target.value)}
                       autoComplete="family-name" />
              </Field>
              <Field label="Geburtstag">
                <input type="date" className={inp} style={inputStyle}
                       value={form.birthday}
                       onChange={(e) => setField('birthday', e.target.value)} />
              </Field>
              <Field label="E-Mail" required error={touched ? (emailError ?? validation.email) : emailError}>
                <input type="email"
                       className={(touched && validation.email) || emailError ? inpError : inp}
                       style={inputStyle}
                       value={form.email}
                       onChange={(e) => setField('email', e.target.value)}
                       autoComplete="email" />
              </Field>
              <Field label="Initial-Passwort" required error={touched ? validation.password : undefined}>
                <input type="password"
                       placeholder="mindestens 8 Zeichen"
                       className={touched && validation.password ? inpError : inp}
                       style={inputStyle}
                       value={form.password}
                       onChange={(e) => setField('password', e.target.value)}
                       autoComplete="new-password" />
                <p className="text-xs text-muted-foreground mt-1">
                  Mindestens 8 Zeichen. Empfohlen: Gross- und Kleinbuchstaben, mindestens eine Zahl oder ein Sonderzeichen.
                  Der User kann das Passwort spaeter selbst unter Einstellungen &raquo; Sicherheit aendern.
                </p>
              </Field>
              <Field label="Telefon">
                <input className={inp} style={inputStyle}
                       value={form.phone}
                       onChange={(e) => setField('phone', e.target.value)}
                       autoComplete="tel" />
              </Field>
              <Field label="Mobil">
                <input className={inp} style={inputStyle}
                       value={form.mobile}
                       onChange={(e) => setField('mobile', e.target.value)}
                       autoComplete="tel" />
              </Field>
            </div>
          </Section>

          <Section title="Anschrift">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Straße + Hausnr.">
                <input className={inp} style={inputStyle}
                       value={form.street}
                       onChange={(e) => setField('street', e.target.value)}
                       autoComplete="street-address" />
              </Field>
              <Field label="Adresszusatz">
                <input className={inp} style={inputStyle}
                       value={form.streetExtra}
                       onChange={(e) => setField('streetExtra', e.target.value)} />
              </Field>
              <div />
              <Field label="PLZ">
                <input className={inp} style={inputStyle}
                       value={form.zip}
                       onChange={(e) => setField('zip', e.target.value)}
                       autoComplete="postal-code" />
              </Field>
              <Field label="Ort">
                <input className={inp} style={inputStyle}
                       value={form.city}
                       onChange={(e) => setField('city', e.target.value)}
                       autoComplete="address-level2" />
              </Field>
              <Field label="Land">
                <select className={inp} style={inputStyle}
                        value={form.countryCode}
                        onChange={(e) => setField('countryCode', e.target.value)}>
                  <option value="DE">Deutschland</option>
                  <option value="AT">Österreich</option>
                  <option value="CH">Schweiz</option>
                  <option value="NL">Niederlande</option>
                  <option value="PL">Polen</option>
                </select>
              </Field>
            </div>
          </Section>

          <Section title="Notfallkontakt">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name">
                <input className={inp} style={inputStyle}
                       value={form.emergencyName}
                       onChange={(e) => setField('emergencyName', e.target.value)} />
              </Field>
              <Field label="Beziehung">
                <input className={inp} style={inputStyle}
                       placeholder="z.B. Ehepartnerin, Mutter"
                       value={form.emergencyRelation}
                       onChange={(e) => setField('emergencyRelation', e.target.value)} />
              </Field>
              <Field label="Telefon">
                <input className={inp} style={inputStyle}
                       value={form.emergencyPhone}
                       onChange={(e) => setField('emergencyPhone', e.target.value)} />
              </Field>
              <Field label="E-Mail" error={touched ? validation.emergencyEmail : undefined}>
                <input type="email"
                       className={touched && validation.emergencyEmail ? inpError : inp}
                       style={inputStyle}
                       value={form.emergencyEmail}
                       onChange={(e) => setField('emergencyEmail', e.target.value)} />
              </Field>
            </div>
          </Section>

          <Section title="Beschäftigung (Basis)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Wochenstunden" error={touched ? validation.weeklyHours : undefined}>
                <input type="number"
                       className={touched && validation.weeklyHours ? inpError : inp}
                       style={inputStyle}
                       value={form.weeklyHours}
                       onChange={(e) => setField('weeklyHours', e.target.value)} />
              </Field>
              <Field label="Stundensatz (€)" error={touched ? validation.hourlyRate : undefined}>
                <input type="number" step="0.01"
                       className={touched && validation.hourlyRate ? inpError : inp}
                       style={inputStyle}
                       value={form.hourlyRate}
                       onChange={(e) => setField('hourlyRate', e.target.value)} />
              </Field>
              <Field label="Status">
                <select className={inp} style={inputStyle}
                        value={form.active ? '1' : '0'}
                        onChange={(e) => setField('active', e.target.value === '1')}>
                  <option value="1">aktiv</option>
                  <option value="0">inaktiv</option>
                </select>
              </Field>
            </div>
            <p className="text-xs text-slate-500">
              Eintrittsdatum, Tages-Soll, Urlaubsanspruch und weitere Beschäftigungs-Details
              werden in der Akte nach dem Speichern gepflegt.
            </p>
          </Section>
        </div>
      )}

      {tab === 'zugriffe' && (
        <div className="space-y-4">
          <Section title="Rolle">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Rolle (users.role)">
                <select className={inp} style={inputStyle}
                        value={form.role}
                        onChange={(e) => setField('role', e.target.value as UserRole)}>
                  {ALL_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                  ))}
                </select>
              </Field>
            </div>
            <p className="text-xs text-slate-500">
              Rolle = Single-Wert in <code>users.role</code>. Modul-Zugriffe ergeben sich aus
              Default-ACL + Gruppen + Person-Overrides.
            </p>
          </Section>

          <Section title="Adressbuch-Gruppen">
            {allGroups.length === 0
              ? <div className="text-sm text-slate-500">Keine Gruppen konfiguriert.</div>
              : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {allGroups.map((g) => {
                    const isMember = selectedGroupIds.has(g.id);
                    return (
                      <label key={g.id}
                             className={`flex items-center gap-2 px-3 py-2 rounded border bg-slate-900/60 cursor-pointer hover:border-slate-600 ${isMember ? 'border-brand-500/40' : 'border-slate-800'}`}>
                        <input type="checkbox"
                               checked={isMember}
                               onChange={() => toggleGroup(g.id)} />
                        <span className="text-sm">{g.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            <div className="text-xs text-slate-500">
              Multi-Mitgliedschaft erlaubt — z.B. „Chef-mit-Stiefeln" = Geschäftsführung + Handwerker.
            </div>
          </Section>

          <Section title="Modul-Zugriffe (ACL)">
            <div className="rounded-lg border border-dashed border-slate-700 bg-slate-900/30 p-4 text-sm text-slate-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
              <div>
                Modul-Overrides können erst nach dem Speichern in der Akte gepflegt werden —
                die ACL-Liste kommt dynamisch aus der Self-Registry pro Person.
              </div>
            </div>
          </Section>
        </div>
      )}

      {tab === 'sicherheit' && (
        <div className="space-y-4">
          <Section title="Sicherheit">
            <div className="rounded-lg border border-amber-700/40 bg-amber-900/10 p-4 text-sm text-amber-100/90">
              <p className="font-medium mb-1">Erst nach dem Anlegen pflegbar</p>
              <p className="text-amber-100/70">
                Letzter Login, aktive Sessions, fehlgeschlagene Logins, 2FA-Reset und
                Konto-Sperre stehen nach dem Speichern in der Akte zur Verfügung. Bis
                dahin: das Initial-Kennwort vergibst du im Stammdaten-Tab.
              </p>
            </div>
          </Section>
        </div>
      )}

      {tab === 'akte' && (
        <div className="space-y-4">
          <Section title="Akte">
            <div className="rounded-lg border border-amber-700/40 bg-amber-900/10 p-4 text-sm text-amber-100/90">
              <p className="font-medium mb-1">Erst nach dem Anlegen verfügbar</p>
              <p className="text-amber-100/70">
                Anträge, Zeiterfassung, Arbeit (Aufgaben + Berichte), Dokumente und
                Verlauf der Person werden erst nach dem ersten Speichern angezeigt —
                bis dahin gibt es noch keinen Datenbestand für diese Person.
              </p>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
